import { max, min } from "date-fns";
import { cs, enUS } from "date-fns/locale";
import { DateConfig } from "src/components/organisms/TableData/types";
import { DateType } from "src/globalTypes";
import { useFormattedMsg } from "src/hooks/useFormatMsg";
import { LangCode } from "src/requests/localization/types";
import { useLocalizationString } from "src/store/localization/useSelectors";
import { isDateInRange } from "src/utils/dateTime";
import { DateViewFormat } from "./FlowioDateInput";
import { ConstraintsType } from "./useDateConfiguration";

enum DateFormat {
  CZECH_DATE_TIME_FORMAT = "dd.MM.yyyy HH:mm",
  CZECH_DATE_FORMAT = "dd.MM.yyyy",
  ENGLISH_DATE_TIME_FORMAT = "yyyy-MM-dd hh:mm a",
  ENGLISH_DATE_FORMAT = "yyyy-MM-dd",
  CZECH_TIME_FORMAT = "HH:mm",
  ENGLISH_TIME_FORMAT = "hh:mm a",
}

// Ohraničují data se 4cifernými roky
const MIN_DATE = new Date("1000-01-01T00:00:00Z");
const MAX_DATE = new Date("9999-12-31T00:00:00Z");

export const getConstraints = (config?: DateConfig): ConstraintsType => {
  const minDate = config?.min
    ? max([MIN_DATE, new Date(config.min)])
    : MIN_DATE;

  const maxDate = config?.max
    ? min([MAX_DATE, new Date(config.max)])
    : MAX_DATE;

  return {
    min: minDate.toISOString(),
    max: maxDate.toISOString(),
  };
};

type GetDateFormat = (dateViewFormat: DateViewFormat) => DateFormat;
export const useGetDateFormat = (): GetDateFormat => {
  const localization = useLocalizationString();
  return (dateViewFormat: DateViewFormat): DateFormat => {
    switch (dateViewFormat) {
      case "time only":
        return localization === "cs"
          ? DateFormat.CZECH_TIME_FORMAT
          : DateFormat.ENGLISH_TIME_FORMAT;
      case "full":
        return localization === "cs"
          ? DateFormat.CZECH_DATE_TIME_FORMAT
          : DateFormat.ENGLISH_DATE_TIME_FORMAT;
      default:
        return localization === "cs"
          ? DateFormat.CZECH_DATE_FORMAT
          : DateFormat.ENGLISH_DATE_FORMAT;
    }
  };
};

export const useLocaleCode = () => {
  const localization = useLocalizationString();

  return localization === LangCode.Czech ? cs : enUS;
};

export const usePlaceholder = (dateViewFormat: DateViewFormat): string => {
  const datePlaceholder = useFormattedMsg("input.date.placeholder");
  const dateTimePlaceholder = useFormattedMsg("input.date_time.placeholder");

  if (dateViewFormat === "day only") return datePlaceholder;
  return dateTimePlaceholder;
};

export const useHelperText = (
  value: DateType | undefined,
  constraints: ConstraintsType
): string | null => {
  const helperTextMsg = useFormattedMsg("template.edit.row");

  if (!value) return null;

  const maxDateString = constraints?.max ?? null;
  const minDateString = constraints?.min ?? null;

  if (
    maxDateString &&
    minDateString &&
    !isDateInRange([minDateString, maxDateString], value)
  ) {
    return helperTextMsg;
  }

  return null;
};

export const isStringUTCDate = (value: string | undefined): boolean => {
  const DateUTCRegex = new RegExp(
    /\b[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}\b/gm
  );
  return DateUTCRegex.test(String(value));
};
